// inspired by: https://m3.material.io/components/dialogs/specs

import {
  useRef,
  useEffect,
  MouseEvent,
  KeyboardEvent,
  SyntheticEvent,
  ReactNode,
  isValidElement,
} from "react";
import styles from "./index.module.css";
import Close from "@/icons/close.svg?react";
import {sanitize} from "@/utils/sanitize";
import RippleButton from "@/components/RippleButton";

export interface ModalDialogProps {
  /**
   * Header content of the dialog.
   */
  header?: string;

  /**
   * Text content of the dialog.
   */
  text?: string;

  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Whether the dialog has a close button.
   */
  hasCloseBtn?: boolean;

  /**
   * Whether the dialog should scroll to the bottom.
   */
  shouldScrollToBottom?: boolean;

  /**
   * Whether the user may close the dialog (default true). When false, Escape,
   * a click on the backdrop and the native `cancel` event (Android Back, a
   * repeated Escape in Chrome) leave it open, and a native `close` while
   * `isOpen` is still true shows it again: the open/close effect below only
   * reacts to `isOpen` changing, so a dialog the browser closed behind its back
   * would otherwise stay gone.
   */
  isDismissable?: boolean;

  /**
   * Function to call when the dialog is closed.
   */
  onClose?: (source: string) => void;

  /**
   * Children of the dialog.
   */
  children?: ReactNode;
}

export const ModalDialog = ({
  header,
  text,
  isOpen,
  hasCloseBtn = true,
  isDismissable = true,
  shouldScrollToBottom,
  onClose,
  children,
}: ModalDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Read by the native `close` handler. Browsers queue that event, but the
  // handler must not depend on it: the ref is set before every `close()` the
  // host asks for, cleanup included (it runs first when `isOpen` turns false),
  // so a dialog the host closed is not shown again even by a `close` fired
  // inside `dialog.close()`.
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    isOpenRef.current = isOpen;
    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      // Runs on unmount and before every re-run when `isOpen` changes; the
      // host is closing it here, so the `close` handler must not reopen it.
      isOpenRef.current = false;
      dialog.close();
    };
  }, [isOpen]);

  // The effect already fires only when the prop flips, so the previous
  // `shouldScrollToBottom = false` writes were redundant: assigning to a prop
  // is discarded on the next render and nothing read it in between.
  useEffect(() => {
    if (contentRef.current && shouldScrollToBottom) {
      contentRef.current.scrollTo({
        left: 0,
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [shouldScrollToBottom]);

  const handleCloseDialog = (source: string) => {
    // if the dialog does not have a close button, do not close it
    if (!hasCloseBtn || !isDismissable) return;

    if (onClose) onClose(source);
    isOpen = false;
  };

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    if (!isDismissable) event.preventDefault();
  };

  const handleNativeClose = (event: SyntheticEvent<HTMLDialogElement>) => {
    if (isDismissable || !isOpenRef.current) return;
    if (!event.currentTarget.open) event.currentTarget.showModal();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") {
      handleCloseDialog("escape");

      // prevent default behavior to avoid closing the dialog unintentionally
      event.preventDefault();
    }
  };

  // close the dialog when clicking outside of it
  const dismiss = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) {
      handleCloseDialog("dismiss");
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onKeyDown={handleKeyDown}
      onCancel={handleCancel}
      onClose={handleNativeClose}
      onClick={(event) => dismiss(event)}
      // Responsive overrides use ! (!important) to win over duplicate
      // Tailwind utilities from the minute-qset-v4 built CSS, which loads
      // after the host CSS and would otherwise override these by cascade order.
      className={[
        "backdrop:bg-surface-dim/90 bg-surface-bright text-on-surface sm:!w-cw-3",
        "m-auto h-full max-h-full w-full max-w-full flex-col open:flex",
        "[@media(min-width:600px)_or_(min-height:600px)]:!h-fit [@media(min-width:600px)_or_(min-height:600px)]:!max-h-[calc(100%-2em-6px)] [@media(min-width:600px)_or_(min-height:600px)]:!rounded-md",
        `${styles.dialog}`,
      ].join(" ")}
    >
      <header className="bg-secondary-container px-g-2 py-g-1 xs:py-g-2 flex flex-row items-center text-xl font-semibold text-white select-none">
        {header}
        <span className="grow"></span>
        {hasCloseBtn && (
          <RippleButton
            variant="icon"
            onClick={() => handleCloseDialog("button")}
          >
            <Close className="size-i-2 fill-white" />
          </RippleButton>
        )}
      </header>
      <div
        ref={contentRef}
        className="p-g-2 sm:p-g-3 xs:leading-normal overflow-auto leading-5"
      >
        {text && <div className="select-none">{sanitize(text)}</div>}
        {text && isValidElement(children) && (
          <div className="h-g-2 xs:h-ch-1"></div>
        )}
        {!text && isValidElement(children) && <div className="h-g-2"></div>}
        <div>{children}</div>
      </div>
    </dialog>
  );
};
